import { Injectable } from '@angular/core';

import { ChartViewportService } from './chart-viewport.service';
import { HistoricalPayload, ProjectService } from './project.service';
import { LivenessFields } from './project-liveness';
import { historicalRangeFor, isBounded } from './project-lifetime';
import { SpatialWindow } from './telemetry-downsample';
import { TELEMETRY_FAMILIES, TelemetryFamily } from './telemetry-store.service';

/**
 * THE OWNER OF A PROJECT OPEN.
 *
 * What this replaces, and why a seventh patch was not the answer
 * --------------------------------------------------------------
 * The historical load used to be a CASCADE OF SIDE EFFECTS. main-layout fetched
 * temperature; the response was pushed into a BehaviorSubject; a subscriber to
 * that subject fetched zone; the zone response went into another BehaviorSubject;
 * a subscriber to THAT fetched loss.
 *
 * So the trigger for a fetch was AN EMISSION ON A REPLAYING SUBJECT, not the
 * completion of a named load. Those are not the same event, and every duplicate
 * came from the gap between them:
 *
 *   - subscribing to a BehaviorSubject replays its seed, which the cascade could
 *     not tell apart from a backfill landing;
 *   - a re-subscribe replays the last value, and one phantom emission upstream
 *     cost a zone read AND a loss read;
 *   - so each hop grew its own latch (historicalIssuedFor, zoneIssuedFor,
 *     lossIssuedFor, lossDataLoaded) and "is it loaded?" had four independent
 *     answers, none authoritative.
 *
 * Six patches tightened those latches. None of them could work, because a latch
 * is an attempt to recognise a real event among phantom ones — and the phantoms
 * are indistinguishable by construction.
 *
 * Here the sequence is a single awaited call chain. Nothing subscribes to a
 * subject in order to decide to fetch. The subjects become what they should
 * always have been: RENDER channels, written once per load, read by charts. With
 * one trigger there is one latch, and it lives here.
 *
 * Order, and why it is what it is
 * -------------------------------
 * Zone's request window comes from the temperature payload's LSS/LES, so zone
 * genuinely cannot be built until temperature has answered. Loss does not: its
 * window comes from loss rows already held, which on a cold open is nothing at
 * all. So the honest shape is two phases, not three:
 *
 *   phase 1   temperature
 *   phase 2   zone ∥ loss          (parallel — the old chain ran them serially
 *                                   only because each was triggered by the
 *                                   previous one's arrival)
 *
 * EMPTY IS AN ANSWER, NOT A FAILURE
 * ---------------------------------
 * The cascade skipped zone and loss whenever temperature had no rows. That guard
 * existed to swallow the replayed empty seed, but it could not tell that apart
 * from a REAL backfill that is genuinely empty — so a project with no temperature
 * data never loaded its other two families and parked forever behind
 * "loss backfill has not landed". That is production's behaviour on beta-dev
 * today, for every project.
 *
 * Removing the subject trigger removes the reason for that guard: a seed can no
 * longer reach here, so an empty payload is always a real answer. Zone and loss
 * are therefore always asked — they are independent datasets — and the caller is
 * told plainly that the project came back empty so it can SAY SO on screen.
 */

/** What one family's backfill did. */
export type FamilyOutcome = 'loaded' | 'empty' | 'failed';

export interface HistoricalLoadResult {
  project: string;
  /** True when a duplicate open was collapsed onto an existing one. */
  suppressed: boolean;
  /** Temperature cursor from the backfill; 0 when there was nothing to seed. */
  cursor: number;
  outcomes: Record<TelemetryFamily, FamilyOutcome>;
  /** Nothing usable arrived and nothing errored — a real, displayable state. */
  empty: boolean;
  /** Nothing usable arrived AND something errored. Distinct from empty. */
  failed: boolean;
  /**
   * Something errored but other families loaded.
   *
   * Kept separate from `failed` because it must NOT blank the screen: the charts
   * that did load are correct and worth showing. It is also the state a
   * server-side partial read will surface as, once the backfill query stops
   * swallowing its exceptions.
   */
  degraded: boolean;
}

@Injectable({ providedIn: 'root' })
export class HistoricalLoadService {
  /** The project whose open has been issued. The ONLY dedupe latch. */
  private issuedFor = '';
  /** The open in progress, so a concurrent caller awaits it instead of re-asking. */
  private inFlight: Promise<HistoricalLoadResult> | null = null;

  /** Opens attempted since load, suppressed or not. Read by specs and traces. */
  static openCount = 0;
  /** Opens that reached the network. The invariant: one per selection. */
  static issuedCount = 0;

  constructor(
    private projectService: ProjectService,
    private viewport: ChartViewportService
  ) {}

  /**
   * Forget the current selection, so the next load is issued rather than
   * collapsed. Called when the selection changes to something else, and on the
   * paths that legitimately need the history again.
   */
  reset(): void {
    this.issuedFor = '';
    this.inFlight = null;
  }

  /**
   * Load every historical family for `project`, exactly once per selection.
   *
   * `force` is for the paths where the same NAME means different bytes — a
   * re-upload, and the scrub-past-retention reload.
   *
   * A concurrent caller gets the in-flight promise rather than a second request:
   * "already loading" and "already loaded" are the same answer to the same
   * question, which is the distinction the old in-flight map could not make.
   */
  load(
    project: string,
    opts: {
      force?: boolean;
      lossWindow?: SpatialWindow | null;
      /** The list row for this project, for its lifetime bounds. */
      info?: LivenessFields | null;
    } = {}
  ): Promise<HistoricalLoadResult> {
    HistoricalLoadService.openCount++;

    if (!project) {
      return Promise.resolve(this.nothing(project, true));
    }

    const force = opts.force === true;

    if (!force) {
      if (this.inFlight) {
        console.log(`HISTORICAL-OPEN collapsed onto the open in flight: ${project}`);
        return this.inFlight;
      }
      if (this.issuedFor === project) {
        console.log(`HISTORICAL-OPEN suppressed, already loaded: ${project}`);
        return Promise.resolve(this.nothing(project, true));
      }
    }

    HistoricalLoadService.issuedCount++;
    console.log(
      `HISTORICAL-OPEN issuing #${HistoricalLoadService.issuedCount} ${project}` +
        (force ? ' (forced)' : '')
    );
    this.issuedFor = project;

    // THE TEMPORAL RANGE FOR A NORMAL OPEN IS UNBOUNDED, both ends.
    //
    // This used to derive a window from the project name and its last-telemetry
    // value. Both were measured losing data — see project-lifetime.ts — and the
    // server-side fix removed the need for either. Routed through
    // historicalRangeFor so the decision has one place to live and one to change.
    const range = historicalRangeFor(project, opts.info ?? null);
    this.projectService.setHistoricalRange(range);
    if (isBounded(range)) {
      console.log(
        `HISTORICAL-OPEN range from=${range.fromMs ?? 'unbounded'} to=${range.toMs ?? 'unbounded'}`
      );
    }

    const run = this.run(project, opts.lossWindow ?? null).finally(() => {
      this.inFlight = null;
    });
    this.inFlight = run;
    return run;
  }

  private async run(
    project: string,
    lossWindow: SpatialWindow | null
  ): Promise<HistoricalLoadResult> {
    const outcomes: Record<TelemetryFamily, FamilyOutcome> = {
      temperature: 'failed',
      zone: 'failed',
      loss: 'failed',
    };
    let cursor = 0;

    // ---- phase 1: temperature, because zone's window comes out of it ----
    let temperature: HistoricalPayload | null = null;
    try {
      temperature = await this.projectService.ensureHistorical(
        project,
        'temperature',
        this.viewport.paramsFor('temperature', null)
      );
      outcomes.temperature = this.outcomeOf('temperature', temperature);
      cursor = this.projectService.getCursor('temperature');
    } catch (err) {
      console.log('HISTORICAL-OPEN temperature failed', err);
      // A failed open must stay re-askable, or the gate becomes a one-shot and a
      // transient error costs the project its data for the rest of the session.
      this.issuedFor = '';
    }

    // ---- phase 2: zone and loss, in parallel ----
    //
    // Asked even when temperature came back empty. They are independent datasets,
    // and the guard that used to skip them existed only to swallow a replayed
    // subject seed that can no longer reach this code.
    const zoneWindow = HistoricalLoadService.windowOf(temperature);
    const [zone, loss] = await Promise.all([
      this.family(project, 'zone', this.viewport.paramsFor('zone', zoneWindow)),
      this.family(project, 'loss', this.viewport.paramsFor('loss', lossWindow)),
    ]);
    outcomes.zone = zone;
    outcomes.loss = loss;

    const anyFailed = TELEMETRY_FAMILIES.some((f) => outcomes[f] === 'failed');
    // USABLE = at least one family came back with rows to draw.
    //
    // The distinction that matters to the screen is not "did anything fail" but
    // "is there anything to show". A loss failure alongside a good temperature
    // and zone load is a degraded project, not a broken one, and blanking two
    // working charts to report the third is a worse answer than the failure.
    const usable = TELEMETRY_FAMILIES.some((f) => outcomes[f] === 'loaded');
    const failed = anyFailed && !usable;
    const empty = !anyFailed && !usable;
    const degraded = anyFailed && usable;
    console.log(
      `HISTORICAL-OPEN settled ${project} ` +
        TELEMETRY_FAMILIES.map((f) => `${f}=${outcomes[f]}`).join(' ') +
        ` cursor=${cursor}`
    );

    return { project, suppressed: false, cursor, outcomes, empty, failed, degraded };
  }

  private async family(
    project: string,
    family: TelemetryFamily,
    params: ReturnType<ChartViewportService['paramsFor']>
  ): Promise<FamilyOutcome> {
    try {
      const payload = await this.projectService.ensureHistorical(
        project,
        family,
        params
      );
      return this.outcomeOf(family, payload);
    } catch (err) {
      console.log(`HISTORICAL-OPEN ${family} failed`, err);
      return 'failed';
    }
  }

  private outcomeOf(
    family: TelemetryFamily,
    payload: HistoricalPayload | null
  ): FamilyOutcome {
    if (!payload) {
      return 'failed';
    }
    return ProjectService.rowsIn(family, payload) > 0 ? 'loaded' : 'empty';
  }

  /** The window zone should be fetched over, read off the temperature payload. */
  static windowOf(payload: HistoricalPayload | null): SpatialWindow | null {
    const d = payload as { LSS?: number; LES?: number } | null;
    if (!d || typeof d.LSS !== 'number' || typeof d.LES !== 'number') {
      return null;
    }
    return d.LES > d.LSS ? { start: d.LSS, end: d.LES } : null;
  }

  private nothing(project: string, suppressed: boolean): HistoricalLoadResult {
    return {
      project,
      suppressed,
      cursor: 0,
      outcomes: { temperature: 'empty', zone: 'empty', loss: 'empty' },
      empty: false,
      failed: false,
      degraded: false,
    };
  }
}
